'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { CartConcessionInputDto } from '@cineamo/api/models/cart-concession/CartConcessionDto.types';
import { CinemaProductDto } from '@cineamo/api/models/cinema-product/CinemaProductDto.types';

import { ApiErrorLike, isApiErrorResponse } from '@cineamo/api/types/ApiErrorResponse.types';

import { isReportableApiFailure } from '@cineamo/api/helpers/api-fallback';
import { setCartConcessions } from '@cineamo/api/resources/cart-concession/cart-concession-api';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { CONCESSION_CATALOG_QUERY_KEY, type ConcessionTab } from '@cineamo/composables/src/hooks/useConcessionCatalog';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconMinus16 } from '@cineamo/ui/src/icons/ic-minus';
import { IconPlus16 } from '@cineamo/ui/src/icons/ic-plus';
import Modal, { ModalProps } from '@cineamo/ui/src/modals/Modal';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import { reportBookingError } from './booking-telemetry-client';
import { concessionErrorKey, withOnlineTicketingFee } from './concession-helper';

type SnackSelectionModalProps = ModalProps & {
    tabs: ConcessionTab[];
};

const stepperButtonClass = cn(
    'flex size-32 shrink-0 items-center justify-center rounded-4 outline-none',
    'bg-[var(--primary-color)] text-[var(--on-primary-color)]',
    'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2'
);

const SnackSelectionModal: React.FC<SnackSelectionModalProps> = ({ tabs, ...modalProps }) => {
    const t = useTranslations('tickets');
    const formatter = useFormat();
    const {
        cartUuid,
        currency,
        concessions,
        setConcessions,
        refreshCartDto,
        onlineTicketingFee,
        concessionPickupLocation
    } = useCartProvider();
    const queryClient = useQueryClient();

    const [quantities, setQuantities] = useState<Record<CinemaProductDto['id'], number>>(() =>
        Object.fromEntries(
            concessions.flatMap(({ cinemaProductId, quantity }) =>
                cinemaProductId === null ? [] : [[cinemaProductId, quantity]]
            )
        )
    );
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const activeTab = tabs[activeTabIndex] ?? tabs[0];
    const format = (cents: number) => formatter.number.formatCurrency(cents / 100, currency);
    /** Catalog gross plus the online ticketing fee, i.e. what the cart will charge per unit. */
    const unitPrice = useCallback(
        (product: CinemaProductDto) => withOnlineTicketingFee(product.price ?? 0, onlineTicketingFee),
        [onlineTicketingFee]
    );
    // A product in several categories is listed in each tab but is one catalog entry.
    const catalogProducts = useMemo(
        () => [...new Map(tabs.flatMap((tab) => tab.products).map((product) => [product.id, product])).values()],
        [tabs]
    );

    const total = useMemo(
        () => catalogProducts.reduce((sum, product) => sum + unitPrice(product) * (quantities[product.id] ?? 0), 0),
        [catalogProducts, quantities, unitPrice]
    );

    const changeQuantity = (productId: CinemaProductDto['id'], delta: number) =>
        setQuantities((previous) => ({ ...previous, [productId]: Math.max(0, (previous[productId] ?? 0) + delta) }));

    const save = async () => {
        setIsSaving(true);
        // Only catalog products are sent. A line of a deleted (`cinemaProductId` null) or since deactivated product
        // cannot be edited here, and leaving it out of the items makes the PUT remove it.
        const items: CartConcessionInputDto[] = catalogProducts
            .map((product) => ({ cinemaProductId: product.id, quantity: quantities[product.id] ?? 0 }))
            .filter(({ quantity }) => quantity > 0);
        try {
            const response = await setCartConcessions(cartUuid, items);
            if (!('_embedded' in response) || !response._embedded)
                throw new Error('Cart concessions response without concessions');
            setConcessions(response._embedded.concessions);
            // The cart's totals include the fee on the new lines; read them before the modal closes.
            await refreshCartDto();
            setIsSaving(false);
            modalProps.onClose?.();
        } catch (caught) {
            setIsSaving(false);
            if (isReportableApiFailure(caught)) {
                reportBookingError({ step: 'concession-save', code: 'failed', error: caught, level: 'warning' });
            }
            // Not an API error: the request itself failed (network).
            const error: ApiErrorLike = isApiErrorResponse(caught) ? caught : {};
            showToast({ toastType: toast.error, messageTitle: t(`error.${concessionErrorKey(error)}`) });
            // A product that vanished or was deactivated since the catalog was loaded: show the current one.
            if (error.status === 400) {
                queryClient.invalidateQueries({ queryKey: [CONCESSION_CATALOG_QUERY_KEY] });
            }
        }
    };

    return (
        <Modal
            {...modalProps}
            className="h-full lg:h-[640px] lg:max-w-[480px]"
            title={t('select-snack')}
            titleCenter
            footer={
                <div className="flex flex-col gap-8 pt-16">
                    <div className="flex items-center justify-between gap-16">
                        <div className="flex flex-col">
                            <span className="text-body-bold-16">{t('total')}</span>
                            <span className="text-body-14 opacity-50">({t('incl-fees-and-taxes')})</span>
                        </div>
                        <span className="text-body-bold-16">{format(total)}</span>
                    </div>
                    <button
                        type="button"
                        onClick={save}
                        disabled={isSaving}
                        className={cn(
                            'flex min-h-48 w-full items-center justify-center gap-8 rounded-4 px-16 text-button-16 outline-none',
                            'bg-[var(--primary-color)] text-[var(--on-primary-color)]',
                            'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2',
                            'disabled:cursor-not-allowed disabled:opacity-50'
                        )}>
                        {isSaving && <IconLoading16 className="animate-spin" />}
                        {t('add-snacks')}
                    </button>
                </div>
            }>
            <p className="shrink-0 pb-8 text-body-14 opacity-70">
                {t(`pickup-${concessionPickupLocation.replace('_', '-')}`)}
            </p>
            <div
                role="tablist"
                className="flex shrink-0 overflow-x-auto border-b border-[rgba(var(--on-non-transparent-surface-color-rgb),0.2)]">
                {tabs.map((tab, index) => (
                    <button
                        key={tab.categoryId ?? 'other'}
                        type="button"
                        role="tab"
                        aria-selected={index === activeTabIndex}
                        onClick={() => setActiveTabIndex(index)}
                        className={cn(
                            'shrink-0 whitespace-nowrap border-b-2 px-16 py-12 text-button-14 outline-none',
                            'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--primary-color)]',
                            index === activeTabIndex
                                ? 'border-[var(--primary-color)]'
                                : 'border-transparent opacity-50 hover:opacity-80'
                        )}>
                        {tab.name ?? t('snack-category-other')}
                    </button>
                ))}
            </div>
            <ul className="flex flex-col divide-y divide-[rgba(var(--on-non-transparent-surface-color-rgb),0.2)]">
                {activeTab?.products.map((product) => {
                    const quantity = quantities[product.id] ?? 0;
                    return (
                        <li
                            key={product.id}
                            className="flex items-center gap-12 py-8">
                            {product.imageUrl ? (
                                // eslint-disable-next-line @next/next/no-img-element -- arbitrary CDN host, not in next/image's allow-list
                                <img
                                    src={product.imageUrl}
                                    alt=""
                                    loading="lazy"
                                    className="size-48 shrink-0 rounded-4 object-cover"
                                />
                            ) : (
                                <div
                                    aria-hidden
                                    className="flex size-48 shrink-0 items-center justify-center rounded-4 bg-[rgba(var(--on-non-transparent-surface-color-rgb),0.1)] text-body-bold-16">
                                    {product.name.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div className="flex min-w-0 grow flex-col">
                                <span className="text-body-bold-16">{product.name}</span>
                                <span className="text-body-14">{format(unitPrice(product))}</span>
                            </div>
                            {quantity > 0 && (
                                <>
                                    <button
                                        type="button"
                                        aria-label={t('decrease-quantity', { name: product.name })}
                                        onClick={() => changeQuantity(product.id, -1)}
                                        className={stepperButtonClass}>
                                        <IconMinus16 />
                                    </button>
                                    <span
                                        className="min-w-24 text-center text-body-bold-16"
                                        aria-live="polite">
                                        {quantity}
                                    </span>
                                </>
                            )}
                            <button
                                type="button"
                                aria-label={t('increase-quantity', { name: product.name })}
                                onClick={() => changeQuantity(product.id, 1)}
                                className={stepperButtonClass}>
                                <IconPlus16 />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </Modal>
    );
};

export default SnackSelectionModal;
